import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  Search,
  Shield,
  ShieldOff,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { UserTable } from '@/features/super-admin/components/users/UserTable'
import { ConfirmModal } from '@/features/super-admin/components/ConfirmModal'
import { AlertModal } from '@/features/super-admin/components/AlertModal'
import {
  type User,
  useAddUserOrganization,
  useAdminResetPassword,
  useBlockUser,
  useConfirmEmail,
  useCreateUser,
  useOrganizationsSelect,
  useRemoveUserOrganization,
  useUnblockUser,
  useUpdateUser,
  useUpdateUserOrganization,
  useUser,
  useUserStats,
  useUsers,
  useImpersonateUser,
} from '@/features/super-admin/hooks/use-users'

export const Route = createFileRoute('/super-admin/users/')({
  component: UsersPage,
})

type OrganizationRole = 'admin' | 'professional' | 'assistant'

function UsersPage() {
  const { onMenuToggle } = useMenuToggle()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [blockingUser, setBlockingUser] = useState<User | null>(null)
  const [blockReason, setBlockReason] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState<{
    email: string
    password: string
  } | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null)
  const [unblockConfirm, setUnblockConfirm] = useState<User | null>(null)
  const [impersonateConfirm, setImpersonateConfirm] = useState<User | null>(null)
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState<User | null>(null)
  const [confirmEmailTarget, setConfirmEmailTarget] = useState<User | null>(null)

  const { data: organizations = [], isLoading: organizationsLoading } = useOrganizationsSelect()
  const { data: usersData, isLoading: usersLoading } = useUsers({
    page,
    page_size: 20,
    search: search || undefined,
    organization_id: organizationFilter || undefined,
    role: (roleFilter || undefined) as OrganizationRole | undefined,
    is_blocked: statusFilter === 'blocked' ? true : statusFilter === 'active' ? false : undefined,
  })
  const { data: stats, isLoading: statsLoading } = useUserStats()
  const blockMutation = useBlockUser()
  const unblockMutation = useUnblockUser()
  const impersonateMutation = useImpersonateUser()
  const adminResetPasswordMutation = useAdminResetPassword()
  const confirmEmailMutation = useConfirmEmail()

  const statCards = [
    {
      title: 'Total de Usuários',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Usuários Ativos',
      value: stats?.active_users || 0,
      icon: Shield,
      color: 'bg-green-500',
    },
    {
      title: 'Usuários Bloqueados',
      value: stats?.blocked_users || 0,
      icon: ShieldOff,
      color: 'bg-red-500',
    },
    {
      title: 'Novos (30 dias)',
      value: stats?.new_users_30d || 0,
      icon: UserPlus,
      color: 'bg-purple-500',
    },
  ]

  const handleBlock = (user: User) => {
    setBlockingUser(user)
    setBlockReason('')
  }

  const confirmBlock = async () => {
    if (!blockingUser || !blockReason.trim()) return
    try {
      await blockMutation.mutateAsync({ id: blockingUser.profile_id, reason: blockReason })
      setBlockingUser(null)
      setBlockReason('')
    } catch (error) {
      setErrorModal({
        title: 'Erro ao bloquear usuário',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      })
    }
  }

  const confirmUnblock = async () => {
    if (!unblockConfirm) return
    try {
      await unblockMutation.mutateAsync(unblockConfirm.profile_id)
      setUnblockConfirm(null)
    } catch (error) {
      setErrorModal({
        title: 'Erro ao desbloquear usuário',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      })
      setUnblockConfirm(null)
    }
  }

  const confirmImpersonate = async () => {
    if (!impersonateConfirm) return
    try {
      const result = await impersonateMutation.mutateAsync(impersonateConfirm.profile_id)
      setImpersonateConfirm(null)
      window.open(`/auth/impersonate?token=${result.session_token}`, '_blank')
    } catch (error) {
      setErrorModal({
        title: 'Erro na impersonação',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      })
      setImpersonateConfirm(null)
    }
  }

  const confirmResetPassword = async () => {
    if (!resetPasswordConfirm) return
    try {
      const result = await adminResetPasswordMutation.mutateAsync(resetPasswordConfirm.profile_id)
      setResetPasswordConfirm(null)
      setShowPasswordModal({
        email: result.email,
        password: result.temporaryPassword,
      })
    } catch (error) {
      setErrorModal({
        title: 'Erro ao resetar senha',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      })
      setResetPasswordConfirm(null)
    }
  }

  const confirmEmailAction = async () => {
    if (!confirmEmailTarget) return
    try {
      await confirmEmailMutation.mutateAsync(confirmEmailTarget.profile_id)
      setConfirmEmailTarget(null)
    } catch (error) {
      setErrorModal({
        title: 'Erro ao confirmar email',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      })
      setConfirmEmailTarget(null)
    }
  }

  const hasFilters = Boolean(search || roleFilter || statusFilter || organizationFilter)

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Usuários"
        subtitle="Gerencie usuários e vínculos com organizações"
        onMenuToggle={onMenuToggle}
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            aria-label="Novo Usuário"
            title="Novo Usuário"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Novo Usuário</span>
          </button>
        }
      />

      <div className="flex-1 overflow-auto p-4 md:p-5 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          {statCards.map((stat) => (
            <div key={stat.title} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-4 h-4 text-white" />
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 leading-snug">{stat.title}</p>
              <p className="text-xl font-semibold text-gray-900 mt-1">
                {statsLoading ? <span className="animate-pulse bg-gray-200 rounded w-14 h-7 block" /> : stat.value.toLocaleString('pt-BR')}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-5">
          <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_repeat(3,minmax(140px,auto))_auto] md:items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs text-gray-500 mb-1">Busca</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome ou email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="min-w-0">
              <label className="block text-xs text-gray-500 mb-1">Organização</label>
              <select
                value={organizationFilter}
                onChange={(event) => {
                  setOrganizationFilter(event.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todas</option>
                {organizationsLoading ? (
                  <option disabled>Carregando...</option>
                ) : (
                  organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs text-gray-500 mb-1">Função</label>
              <select
                value={roleFilter}
                onChange={(event) => {
                  setRoleFilter(event.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todas</option>
                <option value="admin">Admin</option>
                <option value="professional">Profissional</option>
                <option value="assistant">Assistente</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todos</option>
                <option value="active">Ativos</option>
                <option value="blocked">Bloqueados</option>
              </select>
            </div>
            {hasFilters && (
              <button
                onClick={() => {
                  setSearch('')
                  setRoleFilter('')
                  setStatusFilter('')
                  setOrganizationFilter('')
                  setPage(1)
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Limpar Filtros
              </button>
            )}
          </div>
        </div>

        <UserTable
          users={usersData?.data || []}
          loading={usersLoading}
          onBlock={handleBlock}
          onUnblock={setUnblockConfirm}
          onImpersonate={setImpersonateConfirm}
          onResetPassword={setResetPasswordConfirm}
          onConfirmEmail={setConfirmEmailTarget}
          onEdit={setEditingUser}
        />

        {usersData && usersData.pagination.total_pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando página {usersData.pagination.page} de {usersData.pagination.total_pages} ({usersData.pagination.total} usuários)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page === 1}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((current) => Math.min(usersData.pagination.total_pages, current + 1))}
                disabled={page >= usersData.pagination.total_pages}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateUserModal
          organizations={organizations}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(email, password) => {
            setShowCreateModal(false)
            setShowPasswordModal({ email, password })
          }}
          onError={(message) => setErrorModal({ title: 'Erro ao criar usuário', message })}
        />
      )}

      {editingUser && (
        <EditUserModal
          userId={editingUser.profile_id}
          organizations={organizations}
          onClose={() => setEditingUser(null)}
          onError={(message) => setErrorModal({ title: 'Erro ao editar usuário', message })}
        />
      )}

      {blockingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Bloquear Usuário</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Você está prestes a bloquear:</p>
              <p className="font-medium mt-1">{blockingUser.full_name || blockingUser.email}</p>
              <p className="text-sm text-gray-500">{blockingUser.email}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm text-gray-700 mb-1">Motivo do Bloqueio *</label>
              <textarea
                value={blockReason}
                onChange={(event) => setBlockReason(event.target.value)}
                placeholder="Descreva o motivo do bloqueio..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setBlockingUser(null)
                  setBlockReason('')
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmBlock}
                disabled={!blockReason.trim() || blockMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {blockMutation.isPending ? 'Bloqueando...' : 'Confirmar Bloqueio'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!unblockConfirm}
        onClose={() => setUnblockConfirm(null)}
        onConfirm={confirmUnblock}
        title="Desbloquear Usuário"
        message={`Tem certeza que deseja desbloquear ${unblockConfirm?.full_name || unblockConfirm?.email}?`}
        confirmText="Desbloquear"
        variant="info"
        isLoading={unblockMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!impersonateConfirm}
        onClose={() => setImpersonateConfirm(null)}
        onConfirm={confirmImpersonate}
        title="Entrar como Usuário"
        message={`Você está prestes a entrar como ${impersonateConfirm?.full_name || impersonateConfirm?.email}. Uma nova aba será aberta com a sessão do usuário.`}
        confirmText="Continuar"
        variant="warning"
        isLoading={impersonateMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!resetPasswordConfirm}
        onClose={() => setResetPasswordConfirm(null)}
        onConfirm={confirmResetPassword}
        title="Gerar Nova Senha"
        message={`Uma nova senha temporária será gerada para ${resetPasswordConfirm?.full_name || resetPasswordConfirm?.email}.`}
        confirmText="Gerar Senha"
        variant="warning"
        isLoading={adminResetPasswordMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!confirmEmailTarget}
        onClose={() => setConfirmEmailTarget(null)}
        onConfirm={confirmEmailAction}
        title="Confirmar Email"
        message={`Confirmar manualmente o email de ${confirmEmailTarget?.full_name || confirmEmailTarget?.email}?`}
        confirmText="Confirmar Email"
        variant="info"
        isLoading={confirmEmailMutation.isPending}
      />

      <AlertModal
        isOpen={!!errorModal}
        onClose={() => setErrorModal(null)}
        title={errorModal?.title || 'Erro'}
        message={errorModal?.message || ''}
        variant="error"
      />

      {showPasswordModal && (
        <PasswordResultModal
          email={showPasswordModal.email}
          password={showPasswordModal.password}
          onClose={() => setShowPasswordModal(null)}
        />
      )}
    </div>
  )
}

function CreateUserModal({
  organizations,
  onClose,
  onSuccess,
  onError,
}: {
  organizations: Array<{ id: string; name: string }>
  onClose: () => void
  onSuccess: (email: string, password: string) => void
  onError: (message: string) => void
}) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'professional' as OrganizationRole,
    organization_id: '',
  })

  const createMutation = useCreateUser()

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    try {
      const result = await createMutation.mutateAsync({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        role: formData.role,
        organization_id: formData.organization_id || undefined,
        password: formData.password || undefined,
      })

      onSuccess(formData.email, result.temporaryPassword)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro desconhecido')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Novo Usuário</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
            <input
              type="text"
              required
              value={formData.full_name}
              onChange={(event) => setFormData((current) => ({ ...current, full_name: event.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(event) => setFormData((current) => ({ ...current, email: event.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(event) => setFormData((current) => ({ ...current, phone: event.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha <span className="text-gray-400 font-normal">(opcional)</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(event) => setFormData((current) => ({ ...current, password: event.target.value }))}
              placeholder="Deixe em branco para gerar automaticamente"
              minLength={formData.password ? 8 : undefined}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Função *</label>
            <select
              required
              value={formData.role}
              onChange={(event) => setFormData((current) => ({ ...current, role: event.target.value as OrganizationRole }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="professional">Profissional</option>
              <option value="assistant">Assistente</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organização</label>
            <select
              value={formData.organization_id}
              onChange={(event) => setFormData((current) => ({ ...current, organization_id: event.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">Sem organização</option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Criando...' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditUserModal({
  userId,
  organizations,
  onClose,
  onError,
}: {
  userId: string
  organizations: Array<{ id: string; name: string }>
  onClose: () => void
  onError: (message: string) => void
}) {
  const { data: user, isLoading } = useUser(userId)
  const updateUserMutation = useUpdateUser()
  const addOrganizationMutation = useAddUserOrganization()
  const updateOrganizationMutation = useUpdateUserOrganization()
  const removeOrganizationMutation = useRemoveUserOrganization()

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
  })
  const [newMembership, setNewMembership] = useState({
    organizationId: '',
    role: 'professional' as OrganizationRole,
  })

  useEffect(() => {
    if (!user) return
    setFormData({
      full_name: user.full_name || '',
      email: user.email,
      phone: user.phone || '',
    })
  }, [user])

  const availableOrganizations = useMemo(() => {
    if (!user) return organizations
    const activeIds = new Set(user.organizations.filter((membership) => membership.is_active).map((membership) => membership.id))
    return organizations.filter((organization) => !activeIds.has(organization.id))
  }, [organizations, user])

  const handleSaveProfile = async () => {
    if (!user) return
    try {
      await updateUserMutation.mutateAsync({
        profileId: user.profile_id,
        data: {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone || null,
        },
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro ao atualizar usuário')
    }
  }

  const handleAddOrganization = async () => {
    if (!user || !newMembership.organizationId) return

    const existingMembership = user.organizations.find((membership) => membership.id === newMembership.organizationId)

    try {
      if (existingMembership) {
        await updateOrganizationMutation.mutateAsync({
          profileId: user.profile_id,
          organizationId: newMembership.organizationId,
          role: newMembership.role,
          is_active: true,
        })
      } else {
        await addOrganizationMutation.mutateAsync({
          profileId: user.profile_id,
          organizationId: newMembership.organizationId,
          role: newMembership.role,
        })
      }

      setNewMembership({
        organizationId: '',
        role: 'professional',
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro ao adicionar organização')
    }
  }

  const handleMembershipRoleChange = async (organizationId: string, role: OrganizationRole) => {
    if (!user) return
    try {
      await updateOrganizationMutation.mutateAsync({
        profileId: user.profile_id,
        organizationId,
        role,
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro ao atualizar função na organização')
    }
  }

  const handleToggleMembership = async (organizationId: string, isActive: boolean) => {
    if (!user) return
    try {
      await updateOrganizationMutation.mutateAsync({
        profileId: user.profile_id,
        organizationId,
        is_active: isActive,
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro ao atualizar vínculo com a organização')
    }
  }

  const handleRemoveMembership = async (organizationId: string) => {
    if (!user) return
    try {
      await removeOrganizationMutation.mutateAsync({
        profileId: user.profile_id,
        organizationId,
      })
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Erro ao remover organização')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold">Editar Usuário</h3>
            <p className="text-sm text-gray-500">{user?.email || 'Carregando...'}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading || !user ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="p-6 space-y-8">
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">Dados do Perfil</h4>
                  <p className="text-sm text-gray-500">Atualize nome, email e telefone do usuário.</p>
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={updateUserMutation.isPending}
                  className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {updateUserMutation.isPending ? 'Salvando...' : 'Salvar Perfil'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(event) => setFormData((current) => ({ ...current, full_name: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(event) => setFormData((current) => ({ ...current, email: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(event) => setFormData((current) => ({ ...current, phone: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 text-sm text-gray-600">
                  <p><strong>Status de acesso:</strong> {user.has_auth_account ? 'Conta criada' : 'Conta pendente'}</p>
                  <p><strong>Email confirmado:</strong> {user.email_confirmed_at ? 'Sim' : 'Não'}</p>
                  <p><strong>Super admin:</strong> {user.is_super_admin ? 'Sim' : 'Não'}</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Organizações</h4>
                <p className="text-sm text-gray-500">Adicione, remova ou altere o papel do usuário em cada organização.</p>
              </div>

              <div className="space-y-3">
                {user.organizations.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                    Usuário sem vínculos com organizações.
                  </div>
                ) : (
                  user.organizations.map((membership) => (
                    <div
                      key={membership.id}
                      className={`rounded-lg border p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between ${
                        membership.is_active ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50/50'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{membership.name}</p>
                        <p className="text-sm text-gray-500">
                          {membership.is_active ? 'Vínculo ativo' : 'Vínculo inativo'} · {membership.role}
                        </p>
                      </div>
                      <div className="flex flex-col md:flex-row gap-3 md:items-center">
                        <select
                          value={membership.role}
                          onChange={(event) => handleMembershipRoleChange(membership.id, event.target.value as OrganizationRole)}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="admin">Admin</option>
                          <option value="professional">Profissional</option>
                          <option value="assistant">Assistente</option>
                        </select>
                        <button
                          onClick={() => handleToggleMembership(membership.id, !membership.is_active)}
                          className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                          {membership.is_active ? 'Desativar' : 'Reativar'}
                        </button>
                        <button
                          onClick={() => handleRemoveMembership(membership.id)}
                          className="px-3 py-2 text-sm text-red-600 rounded-lg border border-red-200 hover:bg-red-50"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
                <h5 className="font-medium text-gray-900 mb-3">Adicionar organização</h5>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3">
                  <select
                    value={newMembership.organizationId}
                    onChange={(event) => setNewMembership((current) => ({ ...current, organizationId: event.target.value }))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Selecione a organização</option>
                    {availableOrganizations.map((organization) => (
                      <option key={organization.id} value={organization.id}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={newMembership.role}
                    onChange={(event) => setNewMembership((current) => ({ ...current, role: event.target.value as OrganizationRole }))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="professional">Profissional</option>
                    <option value="assistant">Assistente</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    onClick={handleAddOrganization}
                    disabled={!newMembership.organizationId || addOrganizationMutation.isPending || updateOrganizationMutation.isPending}
                    className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {addOrganizationMutation.isPending || updateOrganizationMutation.isPending ? 'Salvando...' : 'Adicionar'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

function PasswordResultModal({
  email,
  password,
  onClose,
}: {
  email: string
  password: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = password
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Senha Temporária</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Senha temporária</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg font-mono text-sm break-all select-all">
                {password}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {copied ? <Mail className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              Informe essa senha ao usuário e peça para ele alterá-la no primeiro login.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
